// src/utils/copilotDraftTypes.js
//
// Central configuration for all instructor copilot draft types.
// Each entry maps a draftType key to:
//   - the AI function to call
//   - how to normalise and extract the result
//   - which UI controls to show in InstructorQuickAiDraftInput
//   - review mode (text | fields | custom) and surface (modal | panel)
//
// The hook (useInstructorCopilot) and panel (AiCopilotPanel) read this config —
// pages only pass draftType + signals + applyFn + instructorId.
//
// callFn contract: (signals, instructorContext, quickInput, instructorProfileClause) → raw AI response
// instructorProfileClause is derived from the instructor's AI profile and injected by useInstructorCopilot.

import {
  generateFollowUpDraft,
  generateProgressNarrative,
  generateNextStepExplanation,
  generateRetentionRiskNarrative,
  generateReviewSummaryFromNotes,
  generateLessonPlanSuggestion,
  normalizeAiInsightResult,
} from './clientAiInsights';

// ─────────────────────────────────────────────────────────────
// Review modes
//   'text'    — single editable textarea (AiGeneratedDraftEditor)
//   'fields'  — main text + named editable fields (editableFieldsFromResult)
//   'custom'  — structured display handled by AiCopilotPanel per type
//               (currently: lesson_plan stages/drills display)
// ─────────────────────────────────────────────────────────────

export const DRAFT_TYPES = {
  // ── Follow-up message ──────────────────────────────────────
  follow_up_message: {
    modalTitle: 'AI Follow-Up Draft',
    generateLabel: 'Generate Follow-Up Draft',
    applyLabel: 'Send Message',
    placeholder: 'Quick notes — tone, focus areas, anything you want the message to cover…',

    // callFn(signals, instructorContext, quickInput, instructorProfileClause) → raw AI response
    callFn: (signals, instructorContext, _quickInput, instructorProfileClause) =>
      generateFollowUpDraft(signals, { instructorContext, instructorProfileClause }),

    normalizeResult: (raw) => normalizeAiInsightResult('followUpDraft', raw),

    // Maps the normalised result to the string shown in the draft editor
    resultToDraft: (normalized) => normalized.draft || '',

    // No extra editable fields — the whole message is the draft
    editableFieldsFromResult: null,

    reviewMode: 'text',
    surface: 'modal',

    showSkillPicker: false,
    showDurationInput: false,
    showTonePicker: true,
    showTagChips: false,
    signalOnlyWarning: true,
  },

  // ── Review summary ─────────────────────────────────────────
  review_summary: {
    modalTitle: 'AI Review Summary',
    generateLabel: 'Generate Review Summary',
    applyLabel: 'Apply to Review',
    placeholder: 'Quick notes — what stood out, key coaching points, areas to address…',

    callFn: (signals, instructorContext, _quickInput, instructorProfileClause) =>
      generateReviewSummaryFromNotes(signals, instructorContext, { instructorProfileClause }),

    normalizeResult: (raw) => normalizeAiInsightResult('reviewSummary', raw),

    // Summary is the primary editable text
    resultToDraft: (normalized) => normalized.summary || '',

    // studentNote and nextStep shown as additional labeled fields
    editableFieldsFromResult: (normalized) => [
      { key: 'studentNote', label: 'Student-facing note', value: normalized.studentNote || '' },
      {
        key: 'nextStepRecommendation',
        label: 'Next step recommendation',
        value: normalized.nextStepRecommendation || '',
      },
    ],

    reviewMode: 'fields',
    surface: 'panel',

    showSkillPicker: false,
    showDurationInput: false,
    showTonePicker: true,
    showTagChips: false,
    signalOnlyWarning: true,
  },

  // ── Lesson plan ────────────────────────────────────────────
  lesson_plan: {
    modalTitle: 'AI Lesson Plan',
    generateLabel: 'Suggest Lesson Plan',
    applyLabel: 'Apply to Lesson',
    placeholder: 'Quick notes — areas to focus on, student challenges, specific goals…',

    // quickInput passed as third arg — lesson plan needs targetSkill + duration
    callFn: (signals, instructorContext, quickInput, instructorProfileClause) =>
      generateLessonPlanSuggestion(signals, {
        targetSkill: quickInput?.targetSkillId || '',
        duration: quickInput?.duration ?? 60,
        instructorContext,
        instructorProfileClause,
      }),

    normalizeResult: (raw) => normalizeAiInsightResult('lessonPlan', raw),

    // No single text field — review rendered by AiCopilotPanel custom renderer
    resultToDraft: null,
    editableFieldsFromResult: null,

    reviewMode: 'custom',
    surface: 'panel',

    showSkillPicker: true,
    showDurationInput: true,
    showTonePicker: false,
    showTagChips: false,
    signalOnlyWarning: true,
  },

  // ── Progress summary (display-only) ───────────────────────
  progress_summary: {
    modalTitle: 'AI Progress Summary',
    generateLabel: 'Generate Summary',
    applyLabel: 'Copy',
    placeholder: 'Quick notes to personalise the summary (optional)…',

    callFn: (signals, instructorContext, _quickInput, instructorProfileClause) =>
      generateProgressNarrative(signals, { instructorContext, instructorProfileClause }),

    normalizeResult: (raw) => normalizeAiInsightResult('progressNarrative', raw),
    resultToDraft: (normalized) => normalized.narrative || '',
    editableFieldsFromResult: null,

    reviewMode: 'text',
    surface: 'panel',

    showSkillPicker: false,
    showDurationInput: false,
    showTonePicker: false,
    showTagChips: false,
    signalOnlyWarning: false,
  },

  // ── Next-step explanation (display-only) ──────────────────
  next_step_explanation: {
    modalTitle: 'AI Next-Step Explanation',
    generateLabel: 'Explain Next Step',
    applyLabel: 'Copy',
    placeholder: 'Quick notes to personalise the explanation (optional)…',

    callFn: (signals, instructorContext, _quickInput, instructorProfileClause) =>
      generateNextStepExplanation(signals, { instructorContext, instructorProfileClause }),

    normalizeResult: (raw) => normalizeAiInsightResult('nextStep', raw),
    resultToDraft: (normalized) => normalized.explanation || '',
    editableFieldsFromResult: null,

    reviewMode: 'text',
    surface: 'panel',

    showSkillPicker: false,
    showDurationInput: false,
    showTonePicker: false,
    showTagChips: false,
    signalOnlyWarning: false,
  },

  // ── Retention note (display-only) ─────────────────────────
  retention_note: {
    modalTitle: 'AI Retention Risk',
    generateLabel: 'Analyse Retention Risk',
    applyLabel: 'Copy',
    placeholder: 'Quick notes to personalise the analysis (optional)…',

    callFn: (signals, instructorContext, _quickInput, instructorProfileClause) =>
      generateRetentionRiskNarrative(signals, { instructorContext, instructorProfileClause }),

    normalizeResult: (raw) => normalizeAiInsightResult('retentionNarrative', raw),
    resultToDraft: (normalized) => normalized.narrative || '',
    editableFieldsFromResult: null,

    reviewMode: 'text',
    surface: 'panel',

    showSkillPicker: false,
    showDurationInput: false,
    showTonePicker: false,
    showTagChips: false,
    signalOnlyWarning: false,
  },
};

/**
 * Returns the config for a given draftType.
 * Throws in development if the type is unknown.
 *
 * @param {string} draftType
 * @returns {object}
 */
export function getDraftTypeConfig(draftType) {
  const config = DRAFT_TYPES[draftType];
  if (!config) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(
        `[copilotDraftTypes] Unknown draftType: "${draftType}". ` +
          `Valid types: ${Object.keys(DRAFT_TYPES).join(', ')}`
      );
    }
    return null;
  }
  return config;
}
